// react
import { useEffect, useState } from 'react';

// shared components
import AzureStyleDateRangeSelector from '@/components/ui/datetime/AzureStyleDateRangeSelector';

// components
import useSearchParams from '@/hooks/useStableSearchParams';

import { DateTime, type DateTime as DateTimeType } from 'luxon';

type Range = [DateTimeType, DateTimeType];

interface DashboardDateRangeSelectorProps {
  paramFrom?: string;
  paramTo?: string;
  onApply?:
    ((range: { from?: string | null; to?: string | null }) => void) | null;
}

// default range: year to date
const defaultRange = (): Range => {
  const to = DateTime.now();
  const from = to.startOf('year');
  return [from, to];
};

const DashboardDateRangeSelector = ({
  paramFrom = 'from',
  paramTo = 'to',
  onApply = null,
}: DashboardDateRangeSelectorProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFrom = searchParams.get(paramFrom);
  const urlTo = searchParams.get(paramTo);

  const [value, setValue] = useState<Range>(() => {
    if (urlFrom && urlTo)
      return [DateTime.fromISO(urlFrom), DateTime.fromISO(urlTo)];
    return defaultRange();
  });

  useEffect(() => {
    // sync from URL when present
    if (urlFrom && urlTo) {
      const f = DateTime.fromISO(urlFrom);
      const t = DateTime.fromISO(urlTo);
      if (!f.equals(value[0]) || !t.equals(value[1])) setValue([f, t]);
    }

    // if missing in URL, write defaults
    if (!urlFrom || !urlTo) {
      const [defFrom, defTo] = defaultRange();
      setSearchParams(
        (prev: URLSearchParams) => {
          const next = new URLSearchParams(prev);
          if (!next.get(paramFrom))
            next.set(paramFrom, defFrom.toISODate() ?? '');
          if (!next.get(paramTo)) next.set(paramTo, defTo.toISODate() ?? '');
          return next;
        },
        { replace: true },
      );
      setValue([defFrom, defTo]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlFrom, urlTo]);

  const handleRangeChange = ({
    from: f,
    to: t,
  }: {
    from: DateTimeType;
    to: DateTimeType;
  }) => {
    setValue([f, t]);
    setSearchParams(
      (prev: URLSearchParams) => {
        const next = new URLSearchParams(prev);
        if (f) next.set(paramFrom, f.toISODate() ?? '');
        if (t) next.set(paramTo, t.toISODate() ?? '');
        return next;
      },
      { replace: true },
    );
    if (onApply) onApply({ from: f?.toISODate(), to: t?.toISODate() });
  };

  return (
    <AzureStyleDateRangeSelector
      from={value?.[0]}
      to={value?.[1]}
      onRangeChange={handleRangeChange}
      includeTime={false}
    />
  );
};

export default DashboardDateRangeSelector;
